import React, { useEffect, useState } from 'react';

import { DangerAlert, HighlightAlert } from '@magoosh/lib/alerts';
import { FontAwesomeIcon, MagooshAIIcon } from '@magoosh/lib/icons';
import useAuthenticityToken from '@magoosh/utils/use_authenticity_token';
import { paths } from 'config/path_helpers';
import fetch from 'utilities/fetch';

import ContextualLink from '@test_prep/app/components/contextual_link';

import colors from '@magoosh/lib/styles/colors.module.scss';

interface Props {
  answerId: number;
  graderType: string;
  onFinished?: () => void;
  renderOnFinished?: boolean;
  status: 'pending_answers' | 'waiting' | 'processing' | 'finished' | 'error';
}

const GradingRequestStatusAlert: React.FC<Props> = (props) => {
  const authenticityToken = useAuthenticityToken();
  const [reportStatus, setReportStatus] = useState<string>(props.status);

  useEffect(() => {
    if (reportStatus === 'new' || reportStatus === 'finished' || reportStatus === 'error') return;

    const poller = setInterval(() => {
      fetch(paths.api.gradingRequestStatus(props.answerId)).then((result) => {
        if (result.gradingRequest.status !== reportStatus) {
          setReportStatus(result.gradingRequest.status);

          if (result.gradingRequest.status === 'finished' && props.onFinished) {
            props.onFinished();
          }

          if (result.gradingRequest.status === 'finished' || result.gradingRequest.status === 'error') {
            clearInterval(poller);
          }
        }
      });
    }, 5000);

    return () => clearInterval(poller)
  }, []);

  if (reportStatus === 'finished' && props.renderOnFinished) {
    return (
      <HighlightAlert>
        <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-s">
          {
            props.graderType === 'human' ? (
              <div>
                Your score report is ready!
              </div>

            ) : (
              <>
                <div className="hidden-sm hidden-xs">
                  <MagooshAIIcon />
                </div>
                <div>
                  Your score report is ready!
                </div>
              </>
            )
          }

          <div className="d-flex flex-1 justify-content-end gap-s">
            <ContextualLink href={paths.answerScoreReport(props.answerId)} style={{ color: colors.purple }}>
              View Score Report
            </ContextualLink>
          </div>
        </div>
      </HighlightAlert>
    );
  } else if (reportStatus === 'finished') {
    return null;
  } else if (reportStatus === 'error') {
    return (
      <DangerAlert>
        <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-s">
          <div>
            We ran into an issue grading your answer. Retry grading and if you are still having trouble, contact us at
            {' '}
            <a className="text-danger" href="mailto:help@magoosh.com">help@magoosh.com</a>.
          </div>

          <div className="d-flex flex-1 justify-content-end gap-s">
            <form action={paths.answerRequestGrading(props.answerId)} method="post">
              <input name="authenticity_token" type="hidden" value={authenticityToken} />
              <input
                className="btn btn-link u-padding-A-n text-danger small text-underline"
                type="submit"
                value="Retry Grading"
              />
            </form>
          </div>
        </div>
      </DangerAlert>
    );
  } else if (reportStatus === 'pending_answers') {
    return (
      <HighlightAlert>
        <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-s">
          <>
            <div className="hidden-sm hidden-xs">
              <MagooshAIIcon />
            </div>
            <div>
              The Magoosh AI Tutor will grade your answer once you complete all questions in the task.
            </div>
          </>
        </div>
      </HighlightAlert>
    );
  } else {
    return (
      <HighlightAlert>
        <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-s">
          {
            props.graderType === 'human' ? (
              <div>
                We're grading your answer. This can take between 24 and 48 hours.
              </div>
            ) : (
              <>
                <div className="hidden-sm hidden-xs">
                  <MagooshAIIcon />
                </div>
                <div>
                  The Magoosh AI Tutor is grading your answer. This can take a minute or two.
                </div>
              </>
            )
          }

          <div className="d-flex align-items-center flex-1 align-self-start align-self-lg-center gap-s justify-content-lg-end small">
            {reportStatus === 'waiting' ? 'Waiting to be Graded' : 'Grading in Progress'}
            <FontAwesomeIcon icon="spinner" className="fa-pulse" />
          </div>
        </div>
      </HighlightAlert>
    );
  }
};

export default GradingRequestStatusAlert;
